import balanceJson from '../balance.json'
import itemsJson from '../items.json'
import ridesJson from '../rides.json'
import { TRACK_FILES } from '../tracks/index'
import type { TrackFileEntry } from '../tracks/index'
import { balanceFileSchema } from './balance.schema'
import type { BalanceFile } from './balance.schema'
import { loadEconomy } from './economy/loadEconomy'
import type { Economy } from './economy/loadEconomy'
import { ConfigError, formatIssues } from './errors'
import { loadItems } from './loadItems'
import type { ItemBalance } from './loadItems'
import { loadRides } from './loadRides'
import type { RideBalance } from './loadRides'
import { loadTracks } from './loadTracks'
import { msToTicks, perSec2ToPerTick2, perSecToPerTick } from './ticks'
import type { TrackDef } from './track.schema'

/** Track hazard numbers (T1.2), durations in ticks. */
export interface HazardBalance {
  readonly bladeStunTicks: number
  readonly bladeTipSizePx: number
  readonly spikeStunTicks: number
  readonly spikeTelegraphTicks: number
  readonly spikeHeightPx: number
  readonly fallingBlockStunTicks: number
  readonly fallingBlockTelegraphTicks: number
  readonly fallingBlockRestTicks: number
  readonly stakeStunTicks: number
  readonly stakeHeightPx: number
  readonly spikedStoneStunTicks: number
  readonly spikedStoneSizePx: number
  readonly trapdoorStunTicks: number
  readonly trapdoorTelegraphTicks: number
  readonly trapdoorSprungTicks: number
  readonly trapdoorStakeHeightPx: number
  readonly barrelSpeedPxPerTick: number
  readonly barrelSizePx: number
  readonly barrelTelegraphTicks: number
  readonly barrelStunTicks: number
}

/** One bot tier (T1.4), durations in ticks. */
export interface BotTier {
  readonly lookaheadPx: number
  readonly reactionMinTicks: number
  readonly reactionMaxTicks: number
  readonly itemDelayMinTicks: number
  readonly itemDelayMaxTicks: number
  readonly trapWhenRankAtMost: number
  /** Per obstacle: act up to this many ticks before the last safe tick (if that still clears). */
  readonly actEarlyMaxTicks: number
  /** Per obstacle: chance of acting `lateMinTicks..lateMaxTicks` after the last safe tick. */
  readonly lateChancePct: number
  readonly lateMinTicks: number
  readonly lateMaxTicks: number
  /** Each bot draws its upper-route chance once from this range (Sky Meadow forks). */
  readonly upperRouteMinPct: number
  readonly upperRouteMaxPct: number
  /** A ledge this much higher than the feet is an upper route. */
  readonly climbMinPx: number
  /** Walking off a drop this deep is trouble (bots jump the gap to stay up). */
  readonly dropAvoidPx: number
  /** Fire the Leader Missile only when the leader is this many ticks of running ahead. */
  readonly missileMinLeadTicks: number
}

/**
 * Validated balance with every duration converted to ticks, speeds to px/tick and
 * accelerations to px/tick². Percentages and pixel sizes pass through unchanged.
 */
export interface Balance {
  readonly baseSpeedPxPerTick: number
  readonly gravityPxPerTick2: number
  readonly jumpVelocityPxPerTick: number
  readonly doubleJumpVelocityPxPerTick: number
  readonly jumpReleaseKeepPct: number
  readonly maxFallSpeedPxPerTick: number
  readonly coyoteTicks: number
  readonly jumpBufferTicks: number
  readonly playerWidthPx: number
  readonly playerHeightPx: number
  readonly rampSnapPx: number
  readonly mudSpeedPct: number
  readonly slowStripPct: number
  readonly bounceVelocityPxPerTick: number
  readonly speedStripTicks: number
  readonly speedStripSpeedPct: number
  readonly crumbleDelayTicks: number
  readonly crumbleRespawnTicks: number
  readonly tumbleSpeedPct: number
  readonly tumblePopVelocityPxPerTick: number
  readonly postStunInvulnTicks: number
  readonly respawnDelayTicks: number
  readonly respawnInvulnTicks: number
  readonly finishRunoutPx: number
  readonly startSpacingPx: number
  /** Countdown before racing starts (room and practice). */
  readonly countdownTicks: number
  readonly match: MatchBalance
  readonly hazards: HazardBalance
  readonly bots: { readonly tier1: BotTier }
  /** `items.json` (T1.3). */
  readonly items: ItemBalance
  /** `rides.json` (PHASE3 T3.10). */
  readonly rides: RideBalance
}

/** Race room flow (T1.6). `…Ms` fields are wall-clock timers, `…Ticks` count sim ticks. */
export interface MatchBalance {
  readonly startFullMs: number
  readonly startMultiMs: number
  readonly startSoloMs: number
  readonly finishGraceTicks: number
  readonly raceCapTicks: number
  readonly resultsHoldMs: number
  readonly botTakeoverTicks: number
  readonly reconnectMs: number
}

export interface Config {
  readonly balance: Balance
  /** Validated track definitions by id. Not sim state; `compileTrack` turns one into sim data. */
  readonly tracks: ReadonlyMap<string, TrackDef>
  /** `economy.json` (D-038): XP, coins, levels and daily tasks. Not gameplay numbers. */
  readonly economy: Economy
}

/** Unvalidated inputs. Omitted parts fall back to the bundled JSON. */
export interface RawConfig {
  balance?: unknown
  items?: unknown
  rides?: unknown
  tracks?: readonly TrackFileEntry[]
  economy?: unknown
}

/**
 * Validate `balance.json`, `items.json`, `rides.json`, the tracks and `economy.json` and convert
 * units (ms → ticks rounding up, px/s → px/tick, px/s² → px/tick²).
 * Throws `ConfigError` whose message names the failing path (e.g. `balance.coyoteMs: Too small`,
 * `tracks.meadow_01.segments.12.type: …`). Pass `raw` in tests; production uses the bundled JSON.
 */
export function loadConfig(raw: RawConfig = {}): Config {
  const balance = loadBalance(
    raw.balance ?? balanceJson,
    loadItems(raw.items ?? itemsJson),
    loadRides(raw.rides ?? ridesJson),
  )
  const tracks = loadTracks(raw.tracks ?? TRACK_FILES, balance)
  const economy = raw.economy === undefined ? loadEconomy() : loadEconomy(raw.economy)
  return Object.freeze({ balance, tracks, economy })
}

function loadBalance(raw: unknown, items: ItemBalance, rides: RideBalance): Balance {
  const parsed = balanceFileSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ConfigError(`Invalid config\n${formatIssues('balance', parsed.error)}`)
  }
  const b = parsed.data
  const h = b.hazards
  const balance: Balance = Object.freeze({
    baseSpeedPxPerTick: perSecToPerTick(b.baseSpeedPxPerSec),
    gravityPxPerTick2: perSec2ToPerTick2(b.gravityPxPerSec2),
    jumpVelocityPxPerTick: perSecToPerTick(b.jumpVelocityPxPerSec),
    doubleJumpVelocityPxPerTick: perSecToPerTick(b.doubleJumpVelocityPxPerSec),
    jumpReleaseKeepPct: b.jumpReleaseKeepPct,
    maxFallSpeedPxPerTick: perSecToPerTick(b.maxFallSpeedPxPerSec),
    coyoteTicks: msToTicks(b.coyoteMs),
    jumpBufferTicks: msToTicks(b.jumpBufferMs),
    playerWidthPx: b.playerWidthPx,
    playerHeightPx: b.playerHeightPx,
    rampSnapPx: b.rampSnapPx,
    mudSpeedPct: b.mudSpeedPct,
    slowStripPct: b.slowStripPct,
    bounceVelocityPxPerTick: perSecToPerTick(b.bounceVelocityPxPerSec),
    speedStripTicks: msToTicks(b.speedStripMs),
    speedStripSpeedPct: b.speedStripSpeedPct,
    crumbleDelayTicks: msToTicks(b.crumbleDelayMs),
    crumbleRespawnTicks: msToTicks(b.crumbleRespawnMs),
    tumbleSpeedPct: b.tumbleSpeedPct,
    tumblePopVelocityPxPerTick: perSecToPerTick(b.tumblePopVelocityPxPerSec),
    postStunInvulnTicks: msToTicks(b.postStunInvulnMs),
    respawnDelayTicks: msToTicks(b.respawnDelayMs),
    respawnInvulnTicks: msToTicks(b.respawnInvulnMs),
    finishRunoutPx: b.finishRunoutPx,
    startSpacingPx: b.startSpacingPx,
    countdownTicks: msToTicks(b.countdownMs),
    match: Object.freeze({
      startFullMs: b.match.startFullMs,
      startMultiMs: b.match.startMultiMs,
      startSoloMs: b.match.startSoloMs,
      finishGraceTicks: msToTicks(b.match.finishGraceMs),
      raceCapTicks: msToTicks(b.match.raceCapMs),
      resultsHoldMs: b.match.resultsHoldMs,
      botTakeoverTicks: msToTicks(b.match.botTakeoverMs),
      reconnectMs: b.match.reconnectMs,
    }),
    hazards: Object.freeze({
      bladeStunTicks: msToTicks(h.bladeStunMs),
      bladeTipSizePx: h.bladeTipSizePx,
      spikeStunTicks: msToTicks(h.spikeStunMs),
      spikeTelegraphTicks: msToTicks(h.spikeTelegraphMs),
      spikeHeightPx: h.spikeHeightPx,
      fallingBlockStunTicks: msToTicks(h.fallingBlockStunMs),
      fallingBlockTelegraphTicks: msToTicks(h.fallingBlockTelegraphMs),
      fallingBlockRestTicks: msToTicks(h.fallingBlockRestMs),
      stakeStunTicks: msToTicks(h.stakeStunMs),
      stakeHeightPx: h.stakeHeightPx,
      spikedStoneStunTicks: msToTicks(h.spikedStoneStunMs),
      spikedStoneSizePx: h.spikedStoneSizePx,
      trapdoorStunTicks: msToTicks(h.trapdoorStunMs),
      trapdoorTelegraphTicks: msToTicks(h.trapdoorTelegraphMs),
      trapdoorSprungTicks: msToTicks(h.trapdoorSprungMs),
      trapdoorStakeHeightPx: h.trapdoorStakeHeightPx,
      barrelSpeedPxPerTick: perSecToPerTick(h.barrelSpeedPxPerSec),
      barrelSizePx: h.barrelSizePx,
      barrelTelegraphTicks: msToTicks(h.barrelTelegraphMs),
      barrelStunTicks: msToTicks(h.barrelStunMs),
    }),
    bots: Object.freeze({ tier1: loadBotTier(b.bots.tier1) }),
    items,
    rides,
  })
  return balance
}

function loadBotTier(t: BalanceFile['bots']['tier1']): BotTier {
  return Object.freeze({
    lookaheadPx: t.lookaheadPx,
    reactionMinTicks: msToTicks(t.reactionMinMs),
    reactionMaxTicks: msToTicks(t.reactionMaxMs),
    itemDelayMinTicks: msToTicks(t.itemDelayMinMs),
    itemDelayMaxTicks: msToTicks(t.itemDelayMaxMs),
    trapWhenRankAtMost: t.trapWhenRankAtMost,
    actEarlyMaxTicks: t.actEarlyMaxTicks,
    lateChancePct: t.lateChancePct,
    lateMinTicks: t.lateMinTicks,
    lateMaxTicks: t.lateMaxTicks,
    upperRouteMinPct: t.upperRouteMinPct,
    upperRouteMaxPct: t.upperRouteMaxPct,
    climbMinPx: t.climbMinPx,
    dropAvoidPx: t.dropAvoidPx,
    missileMinLeadTicks: msToTicks(t.missileMinLeadMs),
  })
}
